import { useState, useCallback, useEffect, useRef } from 'react';
import type { CivicLevel } from '../lib/civicLevelTypes';
import { calculateLevelProgress } from '../lib/civicLevelService';

const ENCOURAGEMENT_MESSAGES = [
  "Nice — you're building momentum.",
  "You're finding your rhythm.",
  "Every small step counts.",
  "You're tuning deeper into the world around you.",
  "Love that. Keep following what resonates.",
  "You're showing up — that matters.",
];

const MILESTONE_THRESHOLDS = [25, 50, 75];

interface UseMomentumCelebrationsReturn {
  showMomentumToast: boolean;
  momentumMessage: string;
  dismissMomentumToast: () => void;
  checkForMilestone: (currentXP: number, currentLevel: CivicLevel) => void;
}

export function useMomentumCelebrations(): UseMomentumCelebrationsReturn {
  const [showMomentumToast, setShowMomentumToast] = useState(false);
  const [momentumMessage, setMomentumMessage] = useState('');
  const shownMilestonesRef = useRef<Set<string>>(new Set());
  const lastPercentRef = useRef<number>(0);

  const getRandomMessage = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * ENCOURAGEMENT_MESSAGES.length);
    return ENCOURAGEMENT_MESSAGES[randomIndex];
  }, []);

  const dismissMomentumToast = useCallback(() => {
    setShowMomentumToast(false);
  }, []);

  const checkForMilestone = useCallback((currentXP: number, currentLevel: CivicLevel) => {
    const progress = calculateLevelProgress(currentXP, currentLevel);
    const currentPercent = Math.floor(progress.percent);

    if (currentPercent === 100) {
      return;
    }

    for (const threshold of MILESTONE_THRESHOLDS) {
      const milestoneKey = `${currentLevel}-${threshold}`;

      if (
        currentPercent >= threshold &&
        lastPercentRef.current < threshold &&
        !shownMilestonesRef.current.has(milestoneKey)
      ) {
        shownMilestonesRef.current.add(milestoneKey);
        setMomentumMessage(getRandomMessage());
        setShowMomentumToast(true);
        break;
      }
    }

    lastPercentRef.current = currentPercent;
  }, [getRandomMessage]);

  useEffect(() => {
    return () => {
      shownMilestonesRef.current.clear();
    };
  }, []);

  return {
    showMomentumToast,
    momentumMessage,
    dismissMomentumToast,
    checkForMilestone,
  };
}
